import { useEffect, useState } from "react";
import {
  PhoneOutgoing, Loader2, Pencil, Trash2, Users,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { LIFECYCLE_TABS, StatusBadge, fmtDate } from "./shared";

export function CampaignLifecycleDialog({ campaign, open, onClose, onDone }) {
  const [step, setStep] = useState("choose");
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  useEffect(() => { if (!open) { setStep("choose"); setConfirmText(""); setBusy(false); } }, [open]);
  if (!campaign) return null;

  const doArchive = async () => {
    setBusy(true);
    try {
      await api.patch(`/outbound/campaigns/${campaign.campaign_id}`, { archived: true, active: false });
      toast.success(`Archived "${campaign.name}"`);
      onDone(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doSoftDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/outbound/campaigns/${campaign.campaign_id}`);
      toast.success(`Deleted "${campaign.name}" (recoverable from the Deleted tab)`);
      onDone(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doHardDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/outbound/campaigns/${campaign.campaign_id}?hard=true`);
      toast.success(`Permanently deleted "${campaign.name}"`);
      onDone(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const isDeleted = !!campaign.deleted_at;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="ai-outbound-lifecycle-dialog">
        {step === "choose" ? (
          <>
            <DialogHeader>
              <DialogTitle>What would you like to do with &quot;{campaign.name}&quot;?</DialogTitle>
              <DialogDescription>
                {isDeleted
                  ? "This campaign is soft-deleted. You can restore it, or permanently delete it (irreversible)."
                  : "Archive is recommended — it hides the campaign from the default list but keeps every attempt and metric intact so analytics still work. Delete is recoverable for 30 days (visible under the Deleted tab); permanent purge removes it forever."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              {!isDeleted && (
                <button onClick={doArchive} disabled={busy} data-testid="ai-outbound-lifecycle-archive"
                        className="w-full rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-left hover:bg-emerald-100 disabled:opacity-50">
                  <div className="font-semibold text-emerald-800 text-sm">Archive (recommended)</div>
                  <div className="text-xs text-emerald-700 mt-0.5">Hidden from Active list. Analytics unchanged. Restore anytime from the Archived tab.</div>
                </button>
              )}
              <button onClick={() => setStep("confirmDelete")} disabled={busy} data-testid="ai-outbound-lifecycle-delete"
                      className="w-full rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-left hover:bg-red-100 disabled:opacity-50">
                <div className="font-semibold text-red-800 text-sm">
                  {isDeleted ? "Permanently delete" : "Delete"}
                </div>
                <div className="text-xs text-red-700 mt-0.5">
                  {isDeleted
                    ? "Removes the campaign and every call attempt from the database. This cannot be undone."
                    : "Marks as deleted. Visible under the Deleted tab; can be restored. Choose Permanently delete on the next screen to purge forever."}
                </div>
              </button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="ai-outbound-lifecycle-cancel">Cancel</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-red-700">
                {isDeleted ? "Permanently delete this campaign?" : "Delete this campaign?"}
              </DialogTitle>
              <DialogDescription>
                {isDeleted
                  ? `Type "${campaign.name}" below to confirm. This will remove the campaign and ALL of its call attempts. This cannot be undone.`
                  : `Sends "${campaign.name}" to the Deleted tab. You can restore it later. To purge permanently instead, restore it first, then use Permanently delete.`}
              </DialogDescription>
            </DialogHeader>
            {isDeleted && (
              <div className="py-2">
                <Label className="text-xs">Type the campaign name to confirm</Label>
                <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                       placeholder={campaign.name} data-testid="ai-outbound-lifecycle-confirm-input" />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("choose")} data-testid="ai-outbound-lifecycle-back">Back</Button>
              {isDeleted ? (
                <Button variant="destructive" onClick={doHardDelete}
                        disabled={busy || confirmText !== campaign.name}
                        data-testid="ai-outbound-lifecycle-confirm-hard">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Permanently delete
                </Button>
              ) : (
                <Button variant="destructive" onClick={doSoftDelete} disabled={busy}
                        data-testid="ai-outbound-lifecycle-confirm-soft">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete (recoverable)
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CampaignsList({ campaigns, loading, statusFilter, onStatusFilterChange, onOpen, onEdit, onReload }) {
  const [lifecycleTarget, setLifecycleTarget] = useState(null);

  const toggleActive = async (c) => {
    try {
      await api.patch(`/outbound/campaigns/${c.campaign_id}`, { active: !c.active });
      toast.success(`Campaign ${!c.active ? "activated" : "paused"}`);
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const restore = async (c) => {
    try {
      await api.post(`/outbound/campaigns/${c.campaign_id}/restore`);
      toast.success(`Restored "${c.name}"`);
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const filterTabs = (
    <div className="mb-3 inline-flex rounded-lg border border-slate-200 p-0.5 bg-white" data-testid="ai-outbound-campaign-filter">
      {LIFECYCLE_TABS.map((t) => (
        <button key={t.value} onClick={() => onStatusFilterChange(t.value)}
                data-testid={`ai-outbound-campaign-filter-${t.value}`}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  statusFilter === t.value ? "bg-primary text-primary-foreground" : "text-slate-600 hover:text-slate-800"
                }`}>{t.label}</button>
      ))}
    </div>
  );

  if (loading) return <>{filterTabs}<div className="text-slate-400" data-testid="ai-outbound-loading">Loading…</div></>;
  if (!campaigns.length) {
    return (
      <>
        {filterTabs}
        <Card className="border border-dashed border-slate-200 p-10 text-center" data-testid="ai-outbound-empty-campaigns">
          <PhoneOutgoing className="mx-auto h-8 w-8 text-slate-300" />
          <div className="mt-2 font-heading text-sm font-semibold text-slate-700">
            {statusFilter === "active" ? "No active campaigns" :
             statusFilter === "archived" ? "No archived campaigns" :
             statusFilter === "deleted" ? "No deleted campaigns" : "No campaigns yet"}
          </div>
          <p className="mx-auto mt-1 max-w-sm text-xs text-slate-400">
            {statusFilter === "active"
              ? "Create a recipient list first, then build a campaign with your prompt, tone, and Calendly link."
              : "Switch to the Active tab to see running campaigns."}
          </p>
        </Card>
      </>
    );
  }
  return (
    <>
      {filterTabs}
      <CampaignLifecycleDialog campaign={lifecycleTarget} open={!!lifecycleTarget}
        onClose={() => setLifecycleTarget(null)} onDone={onReload} />
      <Card className="border border-slate-200 p-0 overflow-hidden" data-testid="ai-outbound-campaigns-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Campaign</th>
              <th className="px-3 py-2 text-left">List</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Active</th>
              <th className="px-3 py-2 text-right">Attempts</th>
              <th className="px-3 py-2 text-right">Booked</th>
              <th className="px-3 py-2 text-left">Created</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {campaigns.map((c) => {
              const isArchived = !!c.archived;
              const isDeleted = !!c.deleted_at;
              const dimmed = isArchived || isDeleted;
              return (
                <tr key={c.campaign_id} className={`hover:bg-slate-50 ${dimmed ? "opacity-70" : ""}`}
                    data-testid={`ai-outbound-campaign-row-${c.campaign_id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => onOpen(c.campaign_id)} className="text-left" data-testid={`ai-outbound-open-${c.campaign_id}`}>
                      <div className="font-medium text-slate-800 hover:text-primary flex items-center gap-2">
                        {c.name}
                        {isArchived && !isDeleted && (
                          <span className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-1.5 py-0 text-[10px] font-medium text-slate-600">Archived</span>
                        )}
                        {isDeleted && (
                          <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-1.5 py-0 text-[10px] font-medium text-red-700">Deleted</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400">{c.owner_name}</div>
                    </button>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{c.list_name || "—"}</td>
                  <td className="px-3 py-2"><StatusBadge status={c.status} /></td>
                  <td className="px-3 py-2">
                    <Switch data-testid={`ai-outbound-active-${c.campaign_id}`}
                      checked={c.active !== false} onCheckedChange={() => toggleActive(c)}
                      disabled={isArchived || isDeleted} />
                  </td>
                  <td className="px-3 py-2 text-right text-slate-700">{c.attempt_count || 0}</td>
                  <td className="px-3 py-2 text-right font-semibold text-emerald-700">{c.booked_count || 0}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(c.created_at)}</td>
                  <td className="px-3 py-2 text-right">
                    {(isArchived || isDeleted) && (
                      <button data-testid={`ai-outbound-restore-${c.campaign_id}`} onClick={() => restore(c)}
                              className="mr-2 text-xs text-emerald-600 hover:text-emerald-800 hover:underline">Restore</button>
                    )}
                    {!isDeleted && (
                      <button data-testid={`ai-outbound-edit-${c.campaign_id}`} onClick={() => onEdit(c.campaign_id)}
                              className="mr-2 text-slate-300 hover:text-primary"><Pencil className="h-4 w-4" /></button>
                    )}
                    <button data-testid={`ai-outbound-delete-${c.campaign_id}`} onClick={() => setLifecycleTarget(c)}
                            className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </>
  );
}
